// src/services/marketplaceService.js
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import axiosService from './axiosService';
import { API } from 'constants/Api';
import systemResourcesPrices from 'configs/systemResourcesPrices.json';
import appService from './appService';
import { useAuthStore } from 'stores/auth';
import translateX from 'utils/translateX';
import { DollarOutlined } from '@ant-design/icons';
import { parseCnpjToDisplay, parseCpfToDisplay } from 'utils/helpers';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const CONTEXT = 'marketplaceService';

// *******************************************************************************
// SERVICE OBJECT
// *******************************************************************************
const marketplaceService = {
  _cachedModules: null,
  _cachedCurrency: null,
};

// *******************************************************************************
// CATALOG METHODS
// *******************************************************************************

/**
 * Get the modules catalog
 * Currently returns from local JSON, will be migrated to API
 * @returns {Promise<Object>} The modules catalog
 */
marketplaceService.getCatalog = async () => {
  // TODO: migrate to API when backend is ready
  // const url = apiUrl.build({ url: API.MARKETPLACE.CATALOG });
  // const [doc] = await axiosService.get({ url, returnAsObject: true });
  // return doc;

  // For now, return from local JSON
  return systemResourcesPrices;
};

/**
 * Get all available modules
 * @returns {Promise<Array>} Array of modules
 */
marketplaceService.getModules = async () => {
  const catalog = await marketplaceService.getCatalog();
  return catalog?.modules || [];
};

/**
 * Get a specific module by UID or key
 * @param {string} identifier - Module UID or key
 * @returns {Promise<Object|null>} The module or null
 */
marketplaceService.getModule = async identifier => {
  const modules = await marketplaceService.getModules();
  return modules.find(m => m.uid === identifier || m.key === identifier) || null;
};

/**
 * Get global resources configuration
 * @returns {Promise<Array>} Array of global resources
 */
marketplaceService.getGlobalResources = async () => {
  const catalog = await marketplaceService.getCatalog();
  return catalog?.globalResources || [];
};

/**
 * Get Jarvisly Credits packages
 * @returns {Promise<Object>} Jarvisly Credits configuration
 */
marketplaceService.getJarvislyCredits = async () => {
  const catalog = await marketplaceService.getCatalog();
  return catalog?.jarvislyCredits || null;
};

/**
 * Get supported currencies
 * @returns {Promise<Array>} Array of currencies
 */
marketplaceService.getCurrencies = async () => {
  const catalog = await marketplaceService.getCatalog();
  return catalog?.currencies || [];
};

// *******************************************************************************
// PRICING METHODS
// *******************************************************************************

/**
 * Get currency by code
 * @param {string} code - Currency code (BRL, USD, etc.)
 * @returns {Promise<Object|null>} Currency object or null
 */
marketplaceService.getCurrency = async code => {
  const currencies = await marketplaceService.getCurrencies();
  return currencies.find(c => c.code === code) || null;
};

/**
 * Format price for display
 * @param {number} value - The price value
 * @param {string} currencyCode - Currency code (BRL, USD, etc.)
 * @returns {string} Formatted price string
 */
marketplaceService.formatPrice = (value, currencyCode = 'BRL') => {
  if (value == null || isNaN(value)) return '-';

  const currencyLocales = {
    BRL: 'pt-BR',
    USD: 'en-US',
    PYG: 'es-PY',
    EUR: 'de-DE',
  };

  const locale = currencyLocales[currencyCode] || 'pt-BR';

  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency: currencyCode,
    minimumFractionDigits: currencyCode === 'PYG' ? 0 : 2,
    maximumFractionDigits: currencyCode === 'PYG' ? 0 : 2,
  }).format(value);
};

/**
 * Get price from a price object for a specific currency
 * @param {Object} priceObj - Object with currency codes as keys
 * @param {string} currencyCode - Currency code
 * @returns {number|null} The price value or null
 */
marketplaceService.getPriceForCurrency = (priceObj, currencyCode = 'BRL') => {
  if (!priceObj || typeof priceObj !== 'object') return null;
  return priceObj[currencyCode] ?? null;
};

// *******************************************************************************
// CALCULATION METHODS
// *******************************************************************************

/**
 * Calculate total monthly price for selected resources in a module
 * @param {Object} module - The module object
 * @param {Array} selectedResourceKeys - Array of resource keys
 * @param {string} currencyCode - Currency code
 * @returns {number} Total monthly price
 */
marketplaceService.calculateModulePrice = (
  module,
  selectedResourceKeys = [],
  currencyCode = 'BRL'
) => {
  if (!module?.resources) return 0;

  return module.resources
    .filter(r => selectedResourceKeys.includes(r.key))
    .reduce((total, resource) => {
      const price = marketplaceService.getPriceForCurrency(resource.monthlyPrice, currencyCode);
      return total + (price || 0);
    }, 0);
};

/**
 * Calculate total for a plan
 * @param {Object} plan - The plan object
 * @param {string} currencyCode - Currency code
 * @returns {number} Plan monthly price
 */
marketplaceService.getPlanPrice = (plan, currencyCode = 'BRL') => {
  if (!plan?.monthlyPrice) return 0;
  return marketplaceService.getPriceForCurrency(plan.monthlyPrice, currencyCode) || 0;
};

/**
 * Calculate overage cost
 * @param {Object} plan - The plan object
 * @param {number} overageCount - Number of overage items
 * @param {string} currencyCode - Currency code
 * @returns {number} Overage cost
 */
marketplaceService.calculateOverageCost = (plan, overageCount, currencyCode = 'BRL') => {
  if (!plan?.overagePrice || overageCount <= 0) return 0;
  const price = marketplaceService.getPriceForCurrency(plan.overagePrice, currencyCode);
  return (price || 0) * overageCount;
};

// *******************************************************************************
// SUBSCRIPTION MODULE METHODS (will call API)
// *******************************************************************************

/**
 * Activate a module for a subscription
 * @param {string} subscriptionId - Subscription ID
 * @param {Object} moduleData - Module activation data
 * @returns {Promise<Object>} Updated subscription
 */
marketplaceService.activateModule = async (subscriptionId, moduleData) => {
  // TODO: implement when backend is ready
  // const url = apiUrl.build({
  //   url: API.MARKETPLACE.ACTIVATE_MODULE,
  //   params: { subscriptionId },
  // });
  // const [doc] = await axiosService.post({ url, data: moduleData });
  // return doc;

  appService.notification({
    flag: 'info',
    title: 'coming_soon',
    description: 'module_activation_coming_soon',
  });

  return null;
};

/**
 * Deactivate a module for a subscription
 * @param {string} subscriptionId - Subscription ID
 * @param {string} moduleUid - Module UID to deactivate
 * @returns {Promise<Object>} Updated subscription
 */
marketplaceService.deactivateModule = async (subscriptionId, moduleUid) => {
  // TODO: implement when backend is ready
  // const url = apiUrl.build({
  //   url: API.MARKETPLACE.DEACTIVATE_MODULE,
  //   params: { subscriptionId, moduleUid },
  // });
  // const [doc] = await axiosService.delete({ url });
  // return doc;

  appService.notification({
    flag: 'info',
    title: 'coming_soon',
    description: 'module_deactivation_coming_soon',
  });

  return null;
};

/**
 * Update module resources for a subscription
 * @param {string} subscriptionId - Subscription ID
 * @param {string} moduleUid - Module UID
 * @param {Array} resources - Array of resource keys
 * @returns {Promise<Object>} Updated subscription
 */
marketplaceService.updateModuleResources = async (subscriptionId, moduleUid, resources) => {
  // TODO: implement when backend is ready
  // const url = apiUrl.build({
  //   url: API.MARKETPLACE.UPDATE_MODULE,
  //   params: { subscriptionId, moduleUid },
  // });
  // const [doc] = await axiosService.put({ url, data: { resources } });
  // return doc;

  appService.notification({
    flag: 'info',
    title: 'coming_soon',
    description: 'module_update_coming_soon',
  });

  return null;
};

/**
 * Purchase Jarvisly Credits
 * @param {string} subscriptionId - Subscription ID
 * @param {string} packageUid - Credit package UID
 * @returns {Promise<Object>} Purchase result
 */
marketplaceService.purchaseCredits = async (subscriptionId, packageUid) => {
  // TODO: implement when backend is ready
  // const url = apiUrl.build({
  //   url: API.MARKETPLACE.PURCHASE_CREDITS,
  //   params: { subscriptionId },
  // });
  // const [doc] = await axiosService.post({ url, data: { packageUid } });
  // return doc;

  appService.notification({
    flag: 'info',
    title: 'coming_soon',
    description: 'credits_purchase_coming_soon',
  });

  return null;
};

// *******************************************************************************
// HELPER METHODS
// *******************************************************************************

/**
 * Check if a module is available (not coming soon)
 * @param {Object} module - The module object
 * @returns {boolean} True if module is available
 */
marketplaceService.isModuleAvailable = module => {
  return module?.status === 'active';
};

/**
 * Check if a module supports trial
 * @param {Object} module - The module object
 * @returns {boolean} True if trial is enabled
 */
marketplaceService.hasTrialAvailable = module => {
  return module?.trial?.enabled === true;
};

/**
 * Get trial duration in days
 * @param {Object} module - The module object
 * @returns {number} Trial duration in days (0 if no trial)
 */
marketplaceService.getTrialDays = module => {
  return module?.trial?.durationDays || 0;
};

/**
 * Get billing type label key
 * @param {string} billingType - Billing type
 * @returns {string} Translation key for the billing type
 */
marketplaceService.getBillingTypeLabel = billingType => {
  const labels = {
    subscription: 'billing_type_subscription',
    pay_per_use: 'billing_type_pay_per_use',
    hybrid: 'billing_type_hybrid',
  };
  return labels[billingType] || billingType;
};

// *******************************************************************************
// CREDITS METHODS
// *******************************************************************************

/**
 * Refresh Jarvisly Credits balance from API and update Zustand store
 * This function can be called from anywhere in the app to update the credits display
 * @returns {Promise<Object>} The balance result { success, balance, breakdown }
 */
marketplaceService.refreshCreditsBalance = async () => {
  try {
    const [response] = await axiosService.get({
      url: API.MARKETPLACE.CREDITS_BALANCE,
      returnAsObject: true,
    });

    if (response?.success) {
      const newBalance = response.balance || 0;

      // Get current state from Zustand
      const state = useAuthStore.getState();
      const { subscription, subscriptions, setSubscription } = state;

      if (subscription && setSubscription) {
        // Create updated subscription with new balance
        const updatedSubscription = {
          ...subscription,
          jarvislyCredits: {
            ...subscription.jarvislyCredits,
            balance: newBalance,
            ...(response.breakdown && { breakdown: response.breakdown }),
          },
        };

        // Update subscriptions array
        const updatedSubscriptions = (subscriptions || []).map(sub =>
          sub._id === subscription._id ? updatedSubscription : sub
        );

        // Update Zustand store
        setSubscription(subscription._id, updatedSubscriptions);
      }

      return {
        success: true,
        balance: newBalance,
        breakdown: response.breakdown,
      };
    }

    return {
      success: false,
      error: response?.error || 'Failed to fetch balance',
    };
  } catch (error) {
    console.error('[marketplaceService] Error refreshing credits balance:', error);
    return {
      success: false,
      error: error.message,
    };
  }
};

/**
 * Get current credits balance (convenience method)
 * @returns {number} Current balance or 0
 */
marketplaceService.getCurrentBalance = () => {
  const state = useAuthStore.getState();
  return state.subscription?.jarvislyCredits?.balance || 0;
};

/**
 * Show notification when credits are debited
 * @param {Object} options - Notification options
 * @param {number} options.creditsDebited - Number of credits debited
 * @param {string} [options.cpfCnpj] - CPF or CNPJ that was enriched
 * @param {number} [options.balanceAfter] - Balance after debit
 * @param {number} [options.duration=8] - Notification duration in seconds
 */
marketplaceService.notifyCreditsDebited = ({
  creditsDebited,
  cpfCnpj,
  balanceAfter,
  duration = 8,
}) => {
  if (!creditsDebited || creditsDebited <= 0) return;

  const isCnpj = cpfCnpj?.length > 11;
  const cpfCnpjDisplay = isCnpj ? parseCnpjToDisplay(cpfCnpj) : parseCpfToDisplay(cpfCnpj);

  appService.notification({
    flag: 'info',

    icon: <DollarOutlined style={{ color: '#1890ff' }} />,
    title: 'credits_debited_title',
    description: translateX(isCnpj ? 'credits_debited_cnpj' : 'credits_debited_cpf', {
      debited: <strong>{creditsDebited}</strong>,
      cpfCnpj: <strong>{cpfCnpjDisplay || '-'}</strong>,
      balance: <strong>{balanceAfter?.toLocaleString() || '0'}</strong>,
    }),
    duration,
  });
};

// *******************************************************************************
// EXPORTS
// *******************************************************************************
export default marketplaceService;
